import { useCallback } from 'react';
import { Linking, Platform } from 'react-native';
import type {
  Attachment,
  AttachmentAnchor,
  Trip,
} from '@my-fullstack-app/itinerary-types';
import { trpc } from '../lib/trpc';
import { useItineraryStore } from '../store/itinerary-store';
import {
  pickAttachmentFile,
  uploadToR2,
  type PickedAttachment,
} from '../lib/attachment-upload';
import { track } from '../lib/analytics';

/**
 * Reading + mutating attachments on a trip (ADR-0060). The
 * attachments router is the canonical writer — we never push these
 * through sync-manager. The slice (`upsertAttachment` /
 * `removeAttachment`) is a local cache the hook keeps in step with
 * the server.
 *
 * Three orchestrations:
 *
 *   - `upload(file, anchor?, label?)` — server `createUploadUrl` →
 *     optimistic store insert (`state: 'pending'`) → R2 PUT →
 *     server `confirm` → store update to `'uploaded'`. Failures
 *     roll back the optimistic insert.
 *
 *   - `download(attachment)` — server `createDownloadUrl` (returns a
 *     short-lived presigned GET) → opens the URL via `Linking`. On
 *     web that triggers a browser download; on native it opens the
 *     PDF / image viewer or shares. Per ADR-0034 we never persist
 *     the URL — fresh mint per click.
 *
 *   - `remove(attachmentId)` and `update(attachmentId, patch)` —
 *     thin wrappers that mutate server-then-store.
 */

export interface AttachmentMutationsApi {
  /** Pick + upload a file. Returns the confirmed Attachment record
   *  on success, or null when the picker was cancelled. */
  upload: (anchor?: AttachmentAnchor | undefined, label?: Attachment['label'] | undefined) => Promise<Attachment | null>;
  /** Pick + upload via a pre-picked file (caller supplies). Same
   *  contract as `upload`; useful when the picker is wired
   *  separately (e.g. a drag-and-drop drop zone on web later). */
  uploadPicked: (
    file: PickedAttachment,
    anchor?: AttachmentAnchor | undefined,
    label?: Attachment['label'] | undefined
  ) => Promise<Attachment | null>;
  /** Open the attachment in the platform viewer / browser. */
  download: (attachment: Attachment) => Promise<void>;
  /** Hard-delete server-side, then drop from local store. */
  remove: (attachmentId: string) => Promise<void>;
  /** Patch label / re-anchor server-side, then mirror locally. */
  update: (
    attachmentId: string,
    patch: { label?: Attachment['label'] | undefined; attachedTo?: AttachmentAnchor | null | undefined}
  ) => Promise<void>;
}

export function useAttachmentMutations(tripId: string): AttachmentMutationsApi {
  const upsertAttachment = useItineraryStore((s) => s.upsertAttachment);
  const removeAttachment = useItineraryStore((s) => s.removeAttachment);
  const utils = trpc.useUtils();
  const createUploadUrl = trpc.attachments.createUploadUrl.useMutation();
  const confirmMutation = trpc.attachments.confirm.useMutation();
  const removeMutation = trpc.attachments.remove.useMutation();
  const updateMutation = trpc.attachments.update.useMutation();

  const uploadPicked = useCallback<AttachmentMutationsApi['uploadPicked']>(
    async (file, anchor, label) => {
      const presignResult = await createUploadUrl.mutateAsync({
        tripId,
        attachment: {
          filename: file.filename,
          mimeType: file.mimeType,
          size: file.size,
          ...(anchor ? { attachedTo: anchor } : {}),
          ...(label ? { label } : {}),
        },
      });
      // Optimistic insert — the row is `state: 'pending'` until confirm.
      upsertAttachment(tripId, presignResult.attachment);
      try {
        await uploadToR2(presignResult.upload.uploadUrl, presignResult.upload.headers, file);
        const confirmed = await confirmMutation.mutateAsync({
          tripId,
          attachmentId: presignResult.attachment.id,
        });
        track('attachment.uploaded', {
          tripId,
          mimeType: file.mimeType,
          size: file.size,
          attachedToKind: anchor?.kind ?? null,
        });
        if (confirmed.attachment) {
          upsertAttachment(tripId, confirmed.attachment);
          return confirmed.attachment;
        }
        return presignResult.attachment;
      } catch (e) {
        // Roll back optimistic insert on any failure (R2 PUT or
        // confirm). Server-side: confirm rejection on size mismatch
        // already cleans up; here we only need to clean up our
        // local cache.
        removeAttachment(tripId, presignResult.attachment.id);
        throw e;
      }
    },
    [confirmMutation, createUploadUrl, removeAttachment, tripId, upsertAttachment]
  );

  const upload = useCallback<AttachmentMutationsApi['upload']>(
    async (anchor, label) => {
      const file = await pickAttachmentFile();
      if (!file) return null;
      return uploadPicked(file, anchor, label);
    },
    [uploadPicked]
  );

  const download = useCallback<AttachmentMutationsApi['download']>(
    async (attachment) => {
      const result = await utils.attachments.createDownloadUrl.fetch({
        tripId,
        attachmentId: attachment.id,
      });
      if (Platform.OS === 'web') {
        // Click an invisible <a download> to surface the filename
        // even when the GET response sets Content-Disposition. Open
        // in a new tab as a fallback when DOM isn't available.
        if (typeof document !== 'undefined') {
          const a = document.createElement('a');
          a.href = result.url;
          a.download = result.filename;
          a.rel = 'noopener noreferrer';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          await Linking.openURL(result.url);
        }
        return;
      }
      // Native: open in the platform viewer (Safari / Chrome custom tab)
      // — handles PDF + images out of the box.
      await Linking.openURL(result.url);
    },
    [tripId, utils]
  );

  const remove = useCallback<AttachmentMutationsApi['remove']>(
    async (attachmentId) => {
      await removeMutation.mutateAsync({ tripId, attachmentId });
      removeAttachment(tripId, attachmentId);
    },
    [removeAttachment, removeMutation, tripId]
  );

  const update = useCallback<AttachmentMutationsApi['update']>(
    async (attachmentId, patch) => {
      const result = await updateMutation.mutateAsync({
        tripId,
        attachmentId,
        patch,
      });
      if (result.attachment) upsertAttachment(tripId, result.attachment);
    },
    [tripId, updateMutation, upsertAttachment]
  );

  return { upload, uploadPicked, download, remove, update };
}

/**
 * Selector hook: returns the attachments anchored to a specific
 * entity within the trip. Pass `{ kind: undefined }` (or omit) to
 * get the trip-level Vault entries (no `attachedTo` set).
 *
 * `kind: 'booking'` plus `entityId` of a journey or stay returns the
 * booking paperwork attached to that reservation; `kind: 'journey'`
 * vs `'booking'` is the same physical entityId — both filter pulls
 * are valid renders for a Journey detail screen.
 */
export function getAttachmentsFor(
  trip: Trip | undefined,
  filter: { kind?: AttachmentAnchor['kind'] | undefined; entityId?: string | undefined}
): Attachment[] {
  if (!trip?.attachments?.length) return [];
  const { kind, entityId } = filter;
  if (!kind && !entityId) {
    // Trip-level Vault filter — anchor undefined.
    return trip.attachments.filter((a) => !a.attachedTo);
  }
  return trip.attachments.filter((a) => {
    if (!a.attachedTo) return false;
    if (kind && a.attachedTo.kind !== kind) return false;
    if (entityId && a.attachedTo.entityId !== entityId) return false;
    return true;
  });
}
